import { zodResolver } from '@hookform/resolvers/zod'
import {
  Avatar,
  Button,
  FormControlLabel,
  Grid,
  MenuItem,
  Switch,
  TextField,
} from '@mui/material'
import dayjs from 'dayjs'
import { useContext, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import InputMask from 'react-input-mask'
import { z } from 'zod'
import { GlobalContext } from '../../../../App'
import { setError, setSuccess } from '../../../../components/alerts'
import { getdoc, metodoPrograma } from '../../../../components/api'
import AutoComplete from '../../../../components/AutoComplete/AutoComplete'
import FileUploader from '../../../../components/FileUploader'
import { numbersOnly } from '../../../../utils/validacoes'
import DateField from '../../../../components/DateField/DateField'
import { getViaCep } from '../../../../utils/api/viacep'

const createPacient = z
  .object({
    idPaciente: z.number().nullable().optional(),
    idLocalAtendimento: z.number(),
    nmPaciente: z
      .string()
      .max(100, { message: 'Nome inválido' })
      .min(3, { message: 'Nome inválido' }),
    dtInclusao: z.string().nullable().optional(),
    nrTelefone: z.string().optional().nullable(),
    dtNascimento: z.any().optional().nullable(),
    dsEmail: z.string().optional().nullable(),
    tpSexo: z.string().optional().nullable(),
    nmMae: z.string().nullable().optional(),
    dsAlergia: z.string().nullable().optional(),
    dsMedicamentoUsoContinuo: z.string().nullable().optional(),
    dsAtividadeFisica: z.string().nullable().optional(),
    snEstrangeiro: z.boolean().optional(),
    cdCpf: z.string().optional().nullable(),
    cdDocumentoEstrangeiro: z.string().optional(),
    dsTamanhoRoupao: z.string().nullable().optional(),
    dsRestricaoAlimentar: z.string().nullable().optional(),
    dsHistoricoProcedimentos: z.string().nullable().optional(),
    dsOcupacao: z.string().nullable().optional(),
    dsComplemento: z.string().optional(),
    nrLogradouro: z.string().optional(),
    dsLogradouro: z.string().optional(),
    cdCep: z.string().optional(),
    dsBairro: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.snEstrangeiro) {
      if (
        !data.cdDocumentoEstrangeiro ||
        data.cdDocumentoEstrangeiro.length < 2
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['cdDocumentoEstrangeiro'],
          message: 'Documento estrangeiro é obrigatório',
        })
      }
    } else {
      const cpf = numbersOnly(data.cdCpf || '')
      if (cpf.length < 11) {
        ctx.addIssue({
          code: 'custom',
          path: ['cdCpf'],
          message: 'CPF deve ter 11 dígitos',
        })
      }
    }
  })

export const DadosCadastrais = ({
  pacienteSelecionado,
  idAtendimento,
  onCancel,
  onConfirm,
  habInfoClinico = true,
}) => {
  const { localAtendimento } = useContext(GlobalContext)
  const [pais, setPais] = useState(
    pacienteSelecionado.acPais || [['BR'], 'Brasil'],
  )
  const [estado, setEstado] = useState(pacienteSelecionado.ufCidade ?? [])
  const [cidade, setCidade] = useState(pacienteSelecionado.idCidade ?? [])
  const [imagemUrl, setImagemUrl] = useState('')
  const [idDocumento, setIdDocumento] = useState(
    pacienteSelecionado?.documento?.[0] ?? '',
  )
  const [dsToken, setDsToken] = useState(
    pacienteSelecionado?.documento?.[1] ?? '',
  )

  const anexarDocumento = async (v) => {
    const documento = v?.documento[0]
    setIdDocumento(documento.idDocumento)
    setDsToken(documento.dsToken)
  }

  const loadDocument = async (token) => {
    try {
      const response = await getdoc(token)
      if (typeof response.blob !== 'function') {
        console.error('Resposta inválida para blob()')
        return
      }
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      setImagemUrl(url)
    } catch (err) {
      console.error('Erro ao carregar documento:', err)
    }
  }

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    resolver: zodResolver(createPacient),
    defaultValues: {
      ...pacienteSelecionado,
      idLocalAtendimento: localAtendimento?.[0],
      snEstrangeiro: pacienteSelecionado?.snEstrangeiro || false,
      cdDocumentoEstrangeiro: pacienteSelecionado?.cdDocumentoEstrangeiro ?? '',
      dtNascimento: pacienteSelecionado?.dtNascimento
        ? dayjs(pacienteSelecionado.dtNascimento)
        : null,
    },
  })

  const buscaPacienteDoc = async (cdDoc, isEstrangeiro = false) => {
    const cdDocFormatado = isEstrangeiro ? cdDoc?.trim() : numbersOnly(cdDoc)

    if (!cdDocFormatado) return

    const {
      status,
      nmPaciente,
      dtNascimento,
      dsEmail,
      nrTelefone,
      idPaciente,
      cdCpf,
      dsLogradouro,
      nrLogradouro,
      dsBairro,
      dsComplemento,
      cdCep,
      acPais,
      acCidade,
      acEstado,
      nmMae,
      tpSexo,
      dsTamanhoRoupao,
      dsOcupacao,
      dsRestricaoAlimentar,
      dsHistoricoProcedimentos,
      dsAlergia,
      dsMedicamentoUsoContinuo,
      dsAtividadeFisica,
    } = await metodoPrograma('buscaPacientePeloDoc', { cdDoc: cdDocFormatado })

    if (status) {
      // Campos de identidade
      if (cdCpf) setValue('cdCpf', cdCpf)
      if (nmPaciente) setValue('nmPaciente', nmPaciente ?? '')
      if (idPaciente) setValue('idPaciente', idPaciente ?? null)
      if (dtNascimento)
        setValue('dtNascimento', dtNascimento ? dayjs(dtNascimento) : null)

      // Contato
      if (dsEmail) setValue('dsEmail', dsEmail ?? '')
      if (nrTelefone) setValue('nrTelefone', nrTelefone ?? '')

      // Endereço
      if (dsLogradouro) setValue('dsLogradouro', dsLogradouro ?? '')
      if (nrLogradouro) setValue('nrLogradouro', nrLogradouro ?? '')
      if (dsBairro) setValue('dsBairro', dsBairro ?? '')
      if (dsComplemento) setValue('dsComplemento', dsComplemento ?? '')
      if (cdCep) setValue('cdCep', cdCep ?? '')

      // Estado e Cidade são state local (não react-hook-form),
      // pois o AutoComplete não usa register — mesma lógica do componente original
      if (acPais) setPais(acPais)
      if (acEstado) setEstado(acEstado)
      if (acCidade) setCidade(acCidade)

      // Dados complementares
      if (nmMae) setValue('nmMae', nmMae ?? '')
      if (tpSexo) setValue('tpSexo', tpSexo ?? '')
      if (dsTamanhoRoupao) setValue('dsTamanhoRoupao', dsTamanhoRoupao ?? '')
      if (dsOcupacao) setValue('dsOcupacao', dsOcupacao ?? '')
      if (dsRestricaoAlimentar)
        setValue('dsRestricaoAlimentar', dsRestricaoAlimentar ?? '')
      if (dsHistoricoProcedimentos)
        setValue('dsHistoricoProcedimentos', dsHistoricoProcedimentos ?? '')
      if (dsAlergia) setValue('dsAlergia', dsAlergia ?? '')
      if (dsMedicamentoUsoContinuo)
        setValue('dsMedicamentoUsoContinuo', dsMedicamentoUsoContinuo ?? '')
      if (dsAtividadeFisica)
        setValue('dsAtividadeFisica', dsAtividadeFisica ?? '')
    }
  }

  const handleViaCep = async (event) => {
    const cdCep = event.target.value
    setValue('cdCep', cdCep, { shouldValidate: true })

    if (numbersOnly(cdCep).length !== 8) return

    const endereco = await getViaCep(cdCep)
    if (!endereco || endereco.erro) return

    setValue('dsLogradouro', endereco.logradouro || '')
    setValue('dsBairro', endereco.bairro || '')
    setEstado([[endereco.uf], endereco.uf])
    setCidade([[Number(endereco.ibge)], endereco.localidade])
  }

  const salvaPaciente = async (values) => {
    try {
      const obj = {
        ...values,
        idAtendimento,
        dtNascimento: values.dtNascimento
          ? dayjs(values.dtNascimento).format('YYYY-MM-DD HH:mm:ss')
          : undefined,
        ufCidade: estado?.[0]?.[0],
        idCidade: cidade?.[0]?.[0],
        cdPais: pais?.[0]?.[0] || 'BR',
        idDocumento: idDocumento || undefined,
        cdCpf: values.cdCpf ? numbersOnly(values.cdCpf) : undefined,
        snEstrangeiro: values.snEstrangeiro ? 'S' : 'N',
        cdDocumentoEstrangeiro: values.cdDocumentoEstrangeiro || '',
      }
      const res = await metodoPrograma('salva', obj, 'CtrlPaciente')
      if (res.status) {
        setSuccess('Paciente cadastrado com sucesso!')
        onConfirm(res.idPaciente)
      } else {
        setError(res?.dsMessage || 'Erro ao salvar paciente')
      }
    } catch (err) {
      setError('Erro ao salvar paciente: ' + err.message)
    }
  }

  useEffect(() => {
    loadDocument(dsToken)
  }, [dsToken])

  return (
    <form onSubmit={handleSubmit(salvaPaciente)}>
      <Grid container spacing={2} alignItems="center">
        {/* Avatar à esquerda */}
        <Grid
          item
          xs={12}
          sm={2}
          display="flex"
          flexDirection="column"
          alignItems="center"
        >
          <Avatar
            alt="Remy Sharp"
            src={imagemUrl || ''}
            sx={{ width: 150, height: 150, mb: 2 }}
          />
          <FileUploader
            title="Alterar"
            path="/avatar/"
            snPublico="N"
            onUploadComplete={anexarDocumento}
          />
        </Grid>

        <Grid item xs={12} sm={10}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={watch('snEstrangeiro') || false}
                    onChange={(_, checked) => {
                      setValue('snEstrangeiro', checked)
                      if (checked) setValue('cdCpf', '')
                    }}
                  />
                }
                label="Estrangeiro"
                labelPlacement="top"
                sx={{ mt: 1, ml: 0 }}
              />
            </Grid>

            {watch('snEstrangeiro') ? (
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Documento Estrangeiro"
                  value={watch('cdDocumentoEstrangeiro') || ''}
                  onChange={(e) =>
                    setValue('cdDocumentoEstrangeiro', e.target.value)
                  }
                  // ─── CORREÇÃO: onBlur dispara a busca com flag isEstrangeiro=true ───
                  // Não aplica numbersOnly — documento pode conter letras e caracteres especiais
                  onBlur={(e) => {
                    const docValue = e.target.value
                    if (docValue?.trim()) {
                      buscaPacienteDoc(docValue, true)
                    }
                  }}
                  InputLabelProps={{
                    shrink: !!watch('cdDocumentoEstrangeiro'),
                  }}
                  variant="outlined"
                  margin="dense"
                  size="small"
                  fullWidth
                  helperText={errors?.cdDocumentoEstrangeiro?.message}
                  error={!!errors?.cdDocumentoEstrangeiro?.message}
                />
              </Grid>
            ) : (
              <Grid item xs={12} sm={4}>
                <InputMask
                  mask="999.999.999-99"
                  maskChar=""
                  value={watch('cdCpf') || ''}
                  onChange={(e) => {
                    const val = e.target.value
                    setValue('cdCpf', val, { shouldValidate: true })
                    // ─── Busca ao completar 11 dígitos (igual ao DadosProposta) ───
                    if (numbersOnly(val).length === 11) {
                      buscaPacienteDoc(val)
                    }
                  }}
                >
                  {() => (
                    <TextField
                      label="CPF"
                      variant="outlined"
                      margin="dense"
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: !!watch('cdCpf') }}
                      helperText={errors?.cdCpf?.message}
                      error={!!errors?.cdCpf?.message}
                    />
                  )}
                </InputMask>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                label="Nome"
                {...register('nmPaciente')}
                InputLabelProps={{ shrink: !!watch('nmPaciente') }}
                variant="outlined"
                margin="dense"
                size="small"
                fullWidth
                helperText={errors?.nmPaciente?.message}
                error={!!errors?.nmPaciente?.message}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                {...register('tpSexo')}
                value={watch('tpSexo') || ''}
                select
                variant="outlined"
                margin="dense"
                size="small"
                label="Sexo"
                fullWidth
                helperText={errors?.tpSexo?.message}
                error={!!errors?.tpSexo?.message}
              >
                <MenuItem value="">Selecione</MenuItem>
                <MenuItem value="M">Masculino</MenuItem>
                <MenuItem value="F">Feminino</MenuItem>
                <MenuItem value="B">Não binário</MenuItem>
                <MenuItem value="N">Não declarou</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <DateField
                label="Data de Nascimento"
                format="DD/MM/YYYY"
                variant="outlined"
                margin="dense"
                size="small"
                InputLabelProps={{ shrink: true }}
                type="date"
                fullWidth
                value={watch('dtNascimento') || null}
                onChange={(v) => setValue('dtNascimento', v)}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <InputMask
                mask="(99) 99999-9999"
                maskChar=""
                value={watch('nrTelefone') || ''}
                onChange={(e) =>
                  setValue('nrTelefone', e.target.value, {
                    shouldValidate: true,
                  })
                }
              >
                {() => (
                  <TextField
                    label="Telefone"
                    InputLabelProps={{ shrink: !!watch('nrTelefone') }}
                    variant="outlined"
                    margin="dense"
                    size="small"
                    fullWidth
                    helperText={errors?.nrTelefone?.message}
                    error={!!errors?.nrTelefone?.message}
                  />
                )}
              </InputMask>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                {...register('dsEmail')}
                type="email"
                variant="outlined"
                InputLabelProps={{ shrink: !!watch('dsEmail') }}
                margin="dense"
                size="small"
                fullWidth
                label="E-mail"
                helperText={errors?.dsEmail?.message}
                error={!!errors?.dsEmail?.message}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                {...register('nmMae')}
                InputLabelProps={{ shrink: !!watch('nmMae') }}
                variant="outlined"
                margin="dense"
                size="small"
                fullWidth
                label="Nome da Mãe"
                helperText={errors?.nmMae?.message}
                error={!!errors?.nmMae?.message}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} sx={{ order: 5 }}>
              <TextField
                {...register('dsLogradouro')}
                InputLabelProps={{ shrink: !!watch('dsLogradouro') }}
                variant="outlined"
                margin="dense"
                size="small"
                fullWidth
                label="Logradouro"
                helperText={errors?.dsLogradouro?.message}
                error={!!errors?.dsLogradouro?.message}
              />
            </Grid>

            <Grid item xs={12} sm={3} sx={{ order: 6 }}>
              <TextField
                {...register('nrLogradouro')}
                variant="outlined"
                margin="dense"
                size="small"
                InputLabelProps={{ shrink: !!watch('nrLogradouro') }}
                fullWidth
                label="Número"
                helperText={errors?.nrLogradouro?.message}
                error={!!errors?.nrLogradouro?.message}
              />
            </Grid>

            <Grid item xs={12} sm={3} sx={{ order: 8 }}>
              <TextField
                {...register('dsBairro')}
                InputLabelProps={{ shrink: !!watch('dsBairro') }}
                variant="outlined"
                margin="dense"
                size="small"
                fullWidth
                label="Bairro"
                helperText={errors?.dsBairro?.message}
                error={!!errors?.dsBairro?.message}
              />
            </Grid>

            <Grid item xs={12} sm={3} sx={{ order: 2 }}>
              {pais?.[0]?.[0] === 'BR' ? (
                <InputMask
                  mask="99999-999"
                  maskChar=""
                  value={watch('cdCep') || ''}
                  onChange={handleViaCep}
                >
                  {() => (
                    <TextField
                      label="CEP"
                      InputLabelProps={{ shrink: !!watch('cdCep') }}
                      variant="outlined"
                      margin="dense"
                      size="small"
                      fullWidth
                      helperText={errors?.cdCep?.message}
                      error={!!errors?.cdCep?.message}
                    />
                  )}
                </InputMask>
              ) : (
                <TextField
                  {...register('cdCep')}
                  label="Código Postal"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 8 }}
                  variant="outlined"
                  margin="dense"
                  size="small"
                  fullWidth
                  helperText={errors?.cdCep?.message}
                  error={!!errors?.cdCep?.message}
                />
              )}
            </Grid>

            <Grid item xs={12} sm={3} sx={{ order: 1 }}>
              <AutoComplete
                InputLabelProps={{ shrink: true }}
                label="País"
                metodo="buscaPais"
                value={pais}
                onChange={(_, val) => {
                  setPais(val || [])
                  setEstado([])
                  setCidade([])
                }}
              />
            </Grid>

            <Grid item xs={12} sm={3} sx={{ order: 3 }}>
              <AutoComplete
                InputLabelProps={{ shrink: true }}
                label="Estado"
                metodo="buscaEstado"
                value={estado}
                disabled={pais?.[0] === undefined}
                searchParams={{ cdPais: pais?.[0]?.[0] }}
                onChange={(_, val) => {
                  if (val === null) {
                    setEstado([])
                  } else {
                    setEstado(val)
                  }
                  setCidade([])
                }}
              />
            </Grid>

            <Grid item xs={12} sm={3} sx={{ order: 4 }}>
              <AutoComplete
                InputLabelProps={{ shrink: true }}
                label="Cidade"
                metodo="buscaCidade"
                value={cidade}
                disabled={pais?.[0] === undefined || estado?.[0] === undefined}
                searchParams={{
                  cdPais: pais?.[0]?.[0],
                  ufCidade: estado?.[0]?.[0],
                }}
                onChange={(_, val) => setCidade(val)}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            {...register('dsComplemento')}
            InputLabelProps={{ shrink: !!watch('dsComplemento') }}
            variant="outlined"
            margin="dense"
            size="small"
            fullWidth
            label="Complemento"
            helperText={errors?.dsComplemento?.message}
            error={!!errors?.dsComplemento?.message}
          />
        </Grid>

        <Grid item xs={6} sm={4}>
          <TextField
            {...register('dsTamanhoRoupao')}
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            margin="dense"
            size="small"
            fullWidth
            label="Tamanho do Roupão"
            helperText={errors?.dsTamanhoRoupao?.message}
            error={!!errors?.dsTamanhoRoupao?.message}
          />
        </Grid>

        <Grid item xs={6} sm={4}>
          <TextField
            {...register('dsOcupacao')}
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            margin="dense"
            size="small"
            fullWidth
            label="Profissão/Ocupação"
            helperText={errors?.dsOcupacao?.message}
            error={!!errors?.dsOcupacao?.message}
          />
        </Grid>

        <Grid item xs={12} sm={12}>
          <TextField
            {...register('dsRestricaoAlimentar')}
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            margin="dense"
            size="small"
            fullWidth
            multiline
            rows={3}
            label="Restrição Alimentar"
            helperText={errors?.dsRestricaoAlimentar?.message}
            error={!!errors?.dsRestricaoAlimentar?.message}
          />
        </Grid>

        <Grid item xs={12} sm={12}>
          <TextField
            {...register('dsHistoricoProcedimentos')}
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            margin="dense"
            size="small"
            fullWidth
            multiline
            rows={3}
            label="Histórico de Procedimentos e Intervenções Cirúrgicas"
            helperText={errors?.dsHistoricoProcedimentos?.message}
            error={!!errors?.dsHistoricoProcedimentos?.message}
          />
        </Grid>

        {habInfoClinico && (
          <>
            <Grid item xs={12} sm={12}>
              <TextField
                {...register('dsAlergia')}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                margin="dense"
                size="small"
                fullWidth
                multiline
                rows={3}
                label="Alergias"
                helperText={errors?.dsAlergia?.message}
                error={!!errors?.dsAlergia?.message}
              />
            </Grid>

            <Grid item xs={12} sm={12}>
              <TextField
                {...register('dsMedicamentoUsoContinuo')}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                margin="dense"
                size="small"
                fullWidth
                multiline
                rows={3}
                label="Medicamento de uso contínuo"
                helperText={errors?.dsMedicamentoUsoContinuo?.message}
                error={!!errors?.dsMedicamentoUsoContinuo?.message}
              />
            </Grid>

            <Grid item xs={12} sm={12}>
              <TextField
                {...register('dsAtividadeFisica')}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                margin="dense"
                size="small"
                fullWidth
                multiline
                rows={3}
                label="Atividade Física"
                helperText={errors?.dsAtividadeFisica?.message}
                error={!!errors?.dsAtividadeFisica?.message}
              />
            </Grid>
          </>
        )}

        <Grid
          item
          xs={12}
          sm={12}
          style={{
            display: 'flex',
            justifyContent: onCancel ? 'space-between' : 'flex-end',
          }}
        >
          {onCancel && (
            <Button variant="contained" onClick={() => onCancel()}>
              Cancelar
            </Button>
          )}
          <Button variant="contained" color="primary" type="submit">
            Salvar
          </Button>
        </Grid>
      </Grid>
    </form>
  )
}
